import { Ship } from './formTypes';
import { useLocalization } from '@ais-ui/locale-management';
import { useMutateShiplistContext } from './MutateShiplistContext';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ShipSearchResult } from '../../../Map/Search';
import {
  Button,
  FormControl,
  FormField,
  FormItem,
  Input,
  Popover,
  PopoverContentNoPortal,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  SearchInput,
} from '@ais-ui/ui-core';
import { cn, isValidIMONumber, isValidMMSINumber } from '@ais-ui/utils';
import { PiMagnifyingGlass, PiMinusThin, PiWarning } from 'react-icons/pi';
import { AISShipSearch } from '../../../Map/Search';
import { useWatch } from 'react-hook-form';

const noop = () => {};

interface SearchPopupProps {
  onClickShip: (ship: ShipSearchResult, index: number) => void;
  shiplistIndex: number;
}

const SearchPopup = ({ onClickShip, shiplistIndex }: SearchPopupProps) => {
  const [searchString, setSearchString] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the input when component mounts
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 150);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <>
      <div className="relative w-full border rounded-md">
        <PiMagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
        <SearchInput
          ref={inputRef}
          className="pl-10 border-none focus-visible:ring-0"
          placeholder="Search vessel..."
          value={searchString}
          onChange={(e) => setSearchString(e.target.value)}
          onClear={() => setSearchString('')}
          autoComplete="off"
        />
      </div>
      {searchString.length > 0 && (
        <div className="mt-2">
          <AISShipSearch
            searchString={searchString}
            onClick={(ship) => {
              onClickShip(ship, shiplistIndex);
            }}
          />
        </div>
      )}
    </>
  );
};

interface ShiplistShipRowProps {
  ship: Ship;
  index: number;
}

export const ShiplistShipRow = ({ ship, index }: ShiplistShipRowProps) => {
  const { messages } = useLocalization();

  const {
    invalidIndices,
    form,
    fields,
    remove,
    update,
    validationErrorMessages,
  } = useMutateShiplistContext();

  // Watch the current form values for real-time updates
  const currentShip = useWatch({
    control: form.control,
    name: `ships.${index}`,
  });

  // Real-time validation based on actual current form values
  const hasMmsiWarning = useMemo(() => {
    const mmsiValue = currentShip?.mmsi;
    // If we have a real value, use real-time validation (this overrides static)
    if (mmsiValue !== undefined && mmsiValue !== null) {
      return !isValidMMSINumber(mmsiValue.toString());
    }
    // If no value, fall back to static validation from context
    return invalidIndices.mmsi.includes(index);
  }, [invalidIndices.mmsi, index, currentShip?.mmsi]);

  const hasImoWarning = useMemo(() => {
    const imoValue = currentShip?.imo;
    // If we have a real value, use real-time validation (this overrides static)
    if (imoValue !== undefined && imoValue !== null) {
      return !isValidIMONumber(imoValue.toString());
    }
    // If no value, fall back to static validation from context
    return invalidIndices.imo.includes(index);
  }, [invalidIndices.imo, index, currentShip?.imo]);

  const hasWarning = useMemo(() => {
    return hasMmsiWarning || hasImoWarning;
  }, [hasMmsiWarning, hasImoWarning]);

  const has409ValidationError = useMemo(() => {
    return Boolean(validationErrorMessages[index]);
  }, [validationErrorMessages, index]);

  const hasError = useMemo(() => {
    return Boolean(
      form.formState.errors?.ships?.[index]?.root ||
      form.formState.errors?.ships?.[index]?.mmsi,
    );
  }, [form.formState.errors?.ships, index]);

  const onClickShip = (ship: ShipSearchResult, index: number) => {
    let mmsi, imo;

    if (typeof ship.mmsi === 'string') {
      mmsi = parseInt(ship.mmsi) || undefined;
    } else if (typeof ship.mmsi === 'number') {
      mmsi = ship.mmsi;
    } else {
      mmsi = undefined;
    }

    if (typeof ship.imo === 'string') {
      imo = parseInt(ship.imo) || undefined;
    } else if (typeof ship.imo === 'number') {
      imo = ship.imo;
    } else {
      imo = undefined;
    }

    const formShip: Ship = {
      id: fields[index].id,
      name: ship.name ? ship.name : '',
      mmsi: mmsi,
      imo: imo,
    };

    form.setValue(`ships.${index}`, formShip);
    update(index, formShip);
  };

  const tooltipWarning = useMemo(() => {
    // First check if we have a specific validation error message from 409 response
    const customErrorMessage = validationErrorMessages[index];
    if (customErrorMessage) {
      return customErrorMessage;
    }

    // Fall back to generic validation messages for client-side validation
    if (hasMmsiWarning) {
      return messages.shiplistShipRowHasMmsiWarningReturn;
    } else if (hasImoWarning) {
      const imoString = ship?.imo?.toString() || '';
      if (imoString.length !== 7) {
        return messages.shiplistShipRowHasImoWarningReturn;
      } else {
        return messages.shiplistShipRowInvalidChecksumImoNumberReturn;
      }
    }
  }, [
    hasMmsiWarning,
    hasImoWarning,
    ship?.imo,
    validationErrorMessages,
    index,
    messages,
  ]);

  return (
    <div className="flex space-x-2 items-center w-full">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={noop}
            className={cn(
              'h-9 w-9 px-2 flex items-center flex-shrink-0',
              'disabled:opacity-100',
              'hover:bg-transparent hover:opacity-100 cursor-default',
              hasError &&
                'font-medium text-destructive hover:text-destructive cursor-pointer w-12',
              has409ValidationError &&
                'font-medium text-destructive hover:text-destructive cursor-pointer w-12',
              hasWarning &&
                !has409ValidationError &&
                'font-medium text-yellow-500 hover:text-yellow-500 cursor-pointer w-12',
            )}
          >
            {(hasError || hasWarning || has409ValidationError) && (
              <PiWarning className="size-4 shrink-0 ml-2 mr-1"></PiWarning>
            )}
            <span className="inline-block">{index + 1}</span>
          </Button>
        </TooltipTrigger>
        {(hasWarning || has409ValidationError) && (
          <TooltipContent>{tooltipWarning}</TooltipContent>
        )}
      </Tooltip>

      {/* Search icon with flex-shrink-0 to prevent squishing */}
      <div className="flex-shrink-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <Popover modal={false}>
              <PopoverTrigger asChild>
                <Button
                  id="search-ship-button"
                  type="button"
                  size="icon"
                  variant="outline"
                  className="h-9 w-9"
                >
                  <PiMagnifyingGlass className="size-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContentNoPortal className="w-[355px]" align="start">
                <SearchPopup onClickShip={onClickShip} shiplistIndex={index} />
              </PopoverContentNoPortal>
            </Popover>
          </TooltipTrigger>
          <TooltipContent>
            {messages.shiplistsSaveSearchForShipTooltip}
          </TooltipContent>
        </Tooltip>
      </div>

      <div className="flex-col space-y-2 w-full min-w-0">
        <FormField
          control={form.control}
          name={`ships.${index}.name`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  {...field}
                  placeholder={messages.shiplistsSaveShipNameLabel}
                  className="flex-grow"
                  style={{ flexBasis: '50%' }}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="flex-col space-y-2 flex-shrink-0">
        <FormField
          control={form.control}
          name={`ships.${index}.mmsi`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  {...field}
                  placeholder={messages.shiplistsSaveMMSILabel}
                  className={cn(
                    'w-32',
                    has409ValidationError &&
                      'border-destructive focus:border-destructive focus:ring-destructive',
                    hasMmsiWarning &&
                      !has409ValidationError &&
                      'border-yellow-500 focus:border-yellow-500 focus:ring-yellow-500',
                  )}
                  type={'number'}
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      field.onChange(null);
                    } else {
                      const numValue = parseInt(value, 10);
                      field.onChange(numValue);
                    }
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
      <div className="flex-col space-y-2 flex-shrink-0">
        <FormField
          control={form.control}
          name={`ships.${index}.imo`}
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <Input
                  {...field}
                  placeholder={messages.shiplistsSaveIMOLabel}
                  className={cn(
                    'w-28',
                    has409ValidationError &&
                      'border-destructive focus:border-destructive focus:ring-destructive',
                    hasImoWarning &&
                      !has409ValidationError &&
                      'border-yellow-500 focus:border-yellow-500 focus:ring-yellow-500',
                  )}
                  type={'number'}
                  value={field.value ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      field.onChange(null);
                    } else {
                      const numValue = parseInt(value, 10);
                      field.onChange(numValue);
                    }
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>

      {/* Remove button at the end */}
      <div className="flex flex-shrink-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              id="remove-ship-button"
              type="button"
              size="icon"
              variant="outline"
              className="h-9 w-9"
              onClick={() => remove(index)}
            >
              <PiMinusThin className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {messages.shiplistsSaveRemoveShipTooltip}
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};
